import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Plus, Minus, ShoppingBag, Trash2 } from "lucide-react";
import { useCart } from "@/lib/cart";
import { whatsappLinks } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

export default function CartDrawer() {
  const { items, count, total, updateQty, remove, open, setOpen } = useCart();
  const navigate = useNavigate();
  const wa = whatsappLinks.cart(items, total);

  const goCheckout = () => {
    setOpen(false);
    // Let the Radix sheet finish its close animation before the route change —
    // navigating in the same tick can leave the dark overlay stuck on screen.
    setTimeout(() => navigate("/checkout"), 260);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="bg-[#0F0F11] border-l border-white/10 text-white w-full sm:max-w-md flex flex-col notranslate" translate="no" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="text-white font-display flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-[#FF2D40]" />
            Warenkorb {count > 0 && <span className="text-zinc-500 text-sm font-normal">({count})</span>}
          </SheetTitle>
          <SheetDescription className="sr-only">Ihre ausgewählten Produkte im Warenkorb</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-4">
          {items.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingBag className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500">Ihr Warenkorb ist leer</p>
              <Link to="/zubehoer" onClick={() => setOpen(false)}>
                <Button className="mt-6 bg-[#DC1928] hover:bg-[#B01829] rounded-full" data-testid="cart-shop-btn">
                  Zubehör entdecken
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <div key={item.sku} className="flex gap-3 p-3 rounded-2xl bg-[#141417] border border-white/5" data-testid={`cart-item-${item.sku}`}>
                  <div className="w-20 h-20 rounded-xl bg-[#0A0A0B] overflow-hidden shrink-0">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-medium text-white text-sm line-clamp-1">{item.name}</div>
                    {item.compatible && <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{item.compatible}</div>}
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center border border-white/10 rounded-full">
                        <button onClick={() => updateQty(item.sku, item.qty - 1)} className="w-7 h-7 flex items-center justify-center text-zinc-400" data-testid={`cart-dec-${item.sku}`}>
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-7 text-center text-xs font-medium numeric">{item.qty}</span>
                        <button onClick={() => updateQty(item.sku, item.qty + 1)} className="w-7 h-7 flex items-center justify-center text-zinc-400" data-testid={`cart-inc-${item.sku}`}>
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="font-display font-semibold text-white numeric text-sm">€{(item.price * item.qty).toFixed(2)}</div>
                    </div>
                  </div>
                  <button onClick={() => remove(item.sku)} className="text-zinc-600 hover:text-red-500 p-1 self-start" data-testid={`cart-rm-${item.sku}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-white/5 pt-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400">Zwischensumme</span>
              <span className="font-display font-semibold text-white numeric">€{total.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400">Versand</span>
              <span className="text-emerald-400">Kostenlos</span>
            </div>
            <div className="flex items-center justify-between border-t border-white/5 pt-3">
              <span className="text-white font-display font-semibold">Gesamt</span>
              <span className="font-display text-2xl font-bold text-white numeric">€{total.toFixed(2)}</span>
            </div>
            <Button onClick={goCheckout} className="w-full bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full h-12 btn-lift glow-red-sm" data-testid="cart-checkout-btn">
              Zur Kasse
            </Button>
            <a
              href={wa.href}
              onClick={(e) => { setOpen(false); wa.onClick(e); }}
              target="_blank"
              rel="noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebd5b] text-white rounded-full h-11 font-medium text-sm transition-colors"
              data-testid="cart-whatsapp-btn"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.498 14.382c-.301-.15-1.767-.872-2.04-.972-.273-.1-.472-.15-.672.15-.197.298-.771.97-.944 1.17-.174.2-.347.226-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.174-.3-.019-.465.13-.615.136-.135.301-.345.451-.523.146-.181.194-.301.297-.501.099-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51-.172-.015-.371-.015-.571-.015-.2 0-.523.074-.797.359-.273.3-1.045 1.02-1.045 2.475s1.07 2.865 1.219 3.075c.149.18 2.105 3.195 5.1 4.485.714.3 1.27.48 1.704.629.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.225-.57-.345m-5.446 7.443h-.016a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Bestellung per WhatsApp
            </a>
            <p className="text-[10px] text-zinc-500 text-center">Stripe · Apple Pay · Google Pay · Klarna · Santander</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
